import { api } from '../lib/api'

export type AIUsage = {
  prompt_tokens?: number
  completion_tokens?: number
  cost_usd?: string
  latency_ms?: number
}

export type AIEditorResponse = {
  content: string
  usage?: AIUsage
  provider?: string
  model?: string
  finish_reason?: string | null
  usage_log_id?: number | null
  ledger_entry_id?: number | null
  cost_usd?: string
  cost_display?: string
  metadata?: Record<string, any>
  plan?: string | null
}

export type AIChatMessage = { role: 'system' | 'user' | 'assistant'; content: string }

export type AIEditorRequest = {
  // Either a single prompt or structured messages
  prompt?: string
  messages?: AIChatMessage[]
  // Optional extras
  body?: string
  tone?: string
  intent?: string
  pinned?: string
  summary?: string
}

export async function runPostAIEditor(input: AIEditorRequest): Promise<AIEditorResponse> {
  // Magazine styling + long edits can take longer than the default 15s axios timeout,
  // so bump this route to 45s while keeping the global default for quicker calls.
  const { data } = await api.post<AIEditorResponse>(
    '/api/posts/ai/editor/',
    input,
    { timeout: 45000 },
  )
  return data
}

// -------- Image generation via AI Gateway route --------

export type AIGeneratedImage = {
  // OpenAI images API may return either base64 or a URL; we surface both if present
  b64_json?: string
  url?: string
  revised_prompt?: string
  // keep any other keys from the provider response
  [key: string]: any
}

export type AICoverImageResponse = {
  images: AIGeneratedImage[]
  usage?: AIUsage
  provider?: string
  model?: string
  metadata?: Record<string, any>
}

export type GenerateCoverImageInput = {
  prompt: string
  size?: string
  quality?: string
  responseFormat?: string
  sourceImage?: string
  sourceImageMime?: string
}

export async function generateCoverImage(input: GenerateCoverImageInput) : Promise<AICoverImageResponse> {
  const payload: any = {
    prompt: input.prompt,
    options: {
      size: input.size || '1024x1024',
      quality: input.quality || 'standard',
      response_format: input.responseFormat || 'b64_json',
    },
  }
  if (input.sourceImage) {
    payload.source_image_b64 = input.sourceImage
    if (input.sourceImageMime) payload.source_image_mime = input.sourceImageMime
  }
  // Image generation/edits can take a while; allow up to 120s before timing out.
  const { data } = await api.post(
    `/api/ai/routes/product_image_polish/dispatch/`,
    payload,
    { timeout: 120000 },
  )
  const output = (data?.output || {}) as any
  const images = Array.isArray(output.images) ? output.images : []
  return {
    images,
    usage: data?.usage,
    provider: data?.provider,
    model: data?.model,
    metadata: data?.metadata,
  }
}

// -------- People Pixe image generator (bio + optional reference photo) --------

export type GeneratePeopleImageInput = {
  prompt: string
  size?: string
  quality?: string
  responseFormat?: string
  sourceImage?: string
  sourceImageMime?: string
  referenceImages?: { image_b64: string; image_mime?: string }[]
}

export async function generatePeopleImage(input: GeneratePeopleImageInput): Promise<AICoverImageResponse> {
  const payload: any = {
    prompt: input.prompt,
    options: {
      size: input.size || '1024x1024',
      quality: input.quality || 'standard',
      response_format: input.responseFormat || 'b64_json',
    },
  }
  if (input.sourceImage) {
    payload.source_image_b64 = input.sourceImage
    if (input.sourceImageMime) payload.source_image_mime = input.sourceImageMime
  }
  if (input.referenceImages && input.referenceImages.length) {
    payload.reference_images = input.referenceImages
  }
  const { data } = await api.post(
    `/api/ai/routes/people_image_generator/dispatch/`,
    payload,
    { timeout: 120000 },
  )
  const output = (data?.output || {}) as any
  const images = Array.isArray(output.images) ? output.images : []
  return {
    images,
    usage: data?.usage,
    provider: data?.provider,
    model: data?.model,
    metadata: data?.metadata,
  }
}
